/**
 * Customer payments API (Razorpay).
 *
 * Endpoints (verified against /openapi.json — tag "Customer Payments"):
 *   POST /api/v1/customer/payments/checkout              → atomic redeem + create order (primary)
 *   POST /api/v1/customer/payments/initiate              → create/refresh an order (no redemption)
 *   POST /api/v1/customer/payments/verify                → verify the checkout signature
 *   GET  /api/v1/customer/payments/{service_request_id}  → current payment status
 *   GET  /api/v1/customer/payments/{service_request_id}/advance           → advance (or null)
 *   POST /api/v1/customer/payments/{service_request_id}/advance/initiate  → Razorpay order for the advance
 *   POST /api/v1/customer/payments/advance/verify        → verify the advance checkout signature
 *
 * The frontend never sees card data — Razorpay's hosted checkout collects it and
 * returns the order/payment/signature triple, which we hand to `verify`. The
 * backend is the source of truth for the amount, reward discount and signature.
 *
 * The pay flow uses `/checkout` (redeem + order in one atomic call) on the pay
 * trigger — NOT `initiate` on modal open. Payment is required whenever the admin
 * has an active gate (`request.payment_required`); on capture the backend
 * auto-deactivates the gate and advances the workflow. See CLAUDE.md →
 * "Payments & rewards".
 */
import {
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { ApiError } from '@finmin/api-client';
import { isApiError, createKeyFactory, type Schemas } from '@finmin/api-client';
import { apiClient } from '@/lib/api';
import { queryClient } from './query-client';
import { serviceRequestKeys } from './service-requests';
import { rewardKeys } from './rewards';
import { billingKeys } from './billing';

// ---------- types -----------------------------------------------------------

/**
 * GST-on-top and advances. Not in the generated schema yet — augment locally
 * until `gen:types` catches up. Both are decimal strings already applied to
 * `net_amount` (the amount charged now).
 */
interface PaymentAdjustments {
  /** GST added on top of the fee; "0.00" when prices include GST. */
  tax_amount: string;
  /** Advance already paid for this booking, deducted from `net_amount`. */
  advance_paid: string;
}

export type InitiatePaymentResponse = Schemas['InitiatePaymentResponse'] & PaymentAdjustments;
export type VerifyPaymentRequest    = Schemas['VerifyPaymentRequest'];
export type VerifyPaymentResponse   = Schemas['VerifyPaymentResponse'];
export type PaymentStatusResponse   = Schemas['PaymentStatusResponse'] & PaymentAdjustments;

/**
 * An advance the admin asked for on this booking. The customer pays it before
 * the final checkout (which answers 400 until it is paid); on capture the
 * backend issues a receipt voucher. Hand-written until `gen:types` catches up.
 *
 * `requested` = set by the admin, not started; `pending` = Razorpay order
 * created; `failed` = last attempt failed (retryable).
 */
export type AdvanceStatus = 'requested' | 'pending' | 'captured' | 'failed' | 'cancelled';

export interface AdvanceResponse {
  id: string;
  service_request_id: string;
  /** Decimal string, e.g. "500.00". */
  amount: string;
  status: AdvanceStatus;
  requested_at: string;
  captured_at: string | null;
  receipt_document_id: string | null;
  receipt_number: string | null;
}

export interface InitiateAdvanceResponse {
  advance_id: string;
  razorpay_order_id: string;
  razorpay_key_id: string;
  amount: string;
  amount_paise: number;
  currency: string;
}

/** Statuses in which the customer still owes the advance. */
export function isAdvanceDue(advance: AdvanceResponse | null | undefined): boolean {
  return advance?.status === 'requested' || advance?.status === 'pending' || advance?.status === 'failed';
}

/**
 * Checkout answers 400 when the customer has no billing state on file. The
 * backend sends no error code, only this `detail` (payment_service.py), so we
 * match its opening phrase to link to the profile's billing details tab.
 */
const MISSING_BILLING_STATE_DETAIL = 'We need your billing state to work out GST.';

export function isMissingBillingDetailsError(err: unknown): boolean {
  return isApiError(err) && err.status === 400 && err.message.startsWith(MISSING_BILLING_STATE_DETAIL);
}

// Atomic redeem + initiate. Not in the generated schema yet — augment locally
// (same fields as `initiate` plus the redemption result) until `gen:types`
// catches up to the backend's Checkout schemas.
export interface CheckoutRequest {
  service_request_id: string;
  /** Reward points to apply; 0 (default) pays full price / undoes a prior redemption. */
  points_to_redeem?: number;
}
export type CheckoutResponse = InitiatePaymentResponse & {
  redeemed_points: number;
  remaining_reward_balance: number;
};

/**
 * When the admin has an active payment gate, the blocked customer POST actions
 * (document upload, submit, rate) return 403 "Payment must be completed to
 * proceed". Returns that backend message (to toast) or `null` for any other
 * error, so callers can fall back to their own generic message.
 */
export function paymentGateMessage(err: unknown): string | null {
  return isApiError(err) && err.status === 403 ? err.message : null;
}

// ---------- query keys ------------------------------------------------------

export const paymentKeys = createKeyFactory('customer.payments', {
  status: (serviceRequestId: string) => ['status', serviceRequestId] as const,
  advance: (serviceRequestId: string) => ['advance', serviceRequestId] as const,
});

// ---------- raw API functions -----------------------------------------------

/** POST /api/v1/customer/payments/initiate */
export async function initiatePayment(serviceRequestId: string) {
  const { data } = await apiClient.post<InitiatePaymentResponse>(
    '/api/v1/customer/payments/initiate',
    { service_request_id: serviceRequestId },
  );
  return data;
}

/**
 * POST /api/v1/customer/payments/checkout — atomic reward redemption + order
 * creation in one call. Replaces the separate `/rewards/redeem` + `/initiate`.
 * Safe to call repeatedly before capture: each call reverses any prior
 * redemption, applies the new points, and creates/refreshes the Razorpay order.
 */
export async function checkout(payload: CheckoutRequest) {
  const { data } = await apiClient.post<CheckoutResponse>(
    '/api/v1/customer/payments/checkout',
    {
      service_request_id: payload.service_request_id,
      points_to_redeem: payload.points_to_redeem ?? 0,
    },
  );
  return data;
}

/** POST /api/v1/customer/payments/verify */
export async function verifyPayment(payload: VerifyPaymentRequest) {
  const { data } = await apiClient.post<VerifyPaymentResponse>(
    '/api/v1/customer/payments/verify',
    payload,
  );
  return data;
}

/** GET /api/v1/customer/payments/{service_request_id} */
export async function getPaymentStatus(serviceRequestId: string, signal?: AbortSignal) {
  const { data } = await apiClient.get<PaymentStatusResponse>(
    `/api/v1/customer/payments/${serviceRequestId}`,
    { signal },
  );
  return data;
}

/**
 * GET /api/v1/customer/payments/{service_request_id}/advance — null when none
 * was set. The backend answers 200 `null` for that; a 404 (e.g. a backend
 * without advances) also means "no advance" so it never blocks the checkout.
 */
export async function getAdvance(serviceRequestId: string, signal?: AbortSignal) {
  try {
    const { data } = await apiClient.get<AdvanceResponse | null>(
      `/api/v1/customer/payments/${serviceRequestId}/advance`,
      { signal },
    );
    return data;
  } catch (err) {
    if (isApiError(err) && err.status === 404) return null;
    throw err;
  }
}

/** POST /api/v1/customer/payments/{service_request_id}/advance/initiate */
export async function initiateAdvance(serviceRequestId: string) {
  const { data } = await apiClient.post<InitiateAdvanceResponse>(
    `/api/v1/customer/payments/${serviceRequestId}/advance/initiate`,
  );
  return data;
}

/** POST /api/v1/customer/payments/advance/verify */
export async function verifyAdvance(payload: VerifyPaymentRequest) {
  const { data } = await apiClient.post<AdvanceResponse>(
    '/api/v1/customer/payments/advance/verify',
    payload,
  );
  return data;
}

// ---------- hooks -----------------------------------------------------------

/**
 * Current payment status for a service request. 404 before any order exists is
 * expected — callers pass `retry: false` / read `isError` rather than treating
 * "no payment yet" as a failure. Disabled until a service-request id is known.
 */
export function usePaymentStatus(
  serviceRequestId: string,
  options?: Omit<UseQueryOptions<PaymentStatusResponse, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<PaymentStatusResponse, ApiError>({
    queryKey: paymentKeys.status(serviceRequestId),
    queryFn: ({ signal }) => getPaymentStatus(serviceRequestId, signal),
    enabled: Boolean(serviceRequestId),
    ...options,
  });
}

/**
 * Create or refresh the Razorpay order. Returns the key id + order id + paise
 * amount the checkout needs. Not cached — call it right before opening checkout
 * so the amount reflects any reward redemption applied since.
 */
export function useInitiatePayment(
  options?: UseMutationOptions<InitiatePaymentResponse, ApiError, string>,
) {
  return useMutation<InitiatePaymentResponse, ApiError, string>({
    mutationFn: initiatePayment,
    ...options,
    onSuccess: (data, serviceRequestId, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.status(serviceRequestId) });
      options?.onSuccess?.(data, serviceRequestId, onMutateResult, context);
    },
  });
}

/**
 * Atomic checkout: redeem points (optional) + create/refresh the Razorpay order
 * in one call. Call it on the pay *trigger* (not on modal open) with the chosen
 * `points_to_redeem`. Invalidates the payment status and reward wallet since
 * both change. Returns the order + authoritative amounts + redemption result.
 */
export function useCheckout(
  options?: UseMutationOptions<CheckoutResponse, ApiError, CheckoutRequest>,
) {
  return useMutation<CheckoutResponse, ApiError, CheckoutRequest>({
    mutationFn: checkout,
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.status(vars.service_request_id) });
      queryClient.invalidateQueries({ queryKey: rewardKeys.wallet() });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

/** Refresh this request's bills and the full bills list (not states/details). */
function invalidateBills(serviceRequestId: string) {
  queryClient.invalidateQueries({ queryKey: billingKeys.bills(serviceRequestId) });
  queryClient.invalidateQueries({ queryKey: billingKeys.bills() });
}

/**
 * Verify the signature after the user completes Razorpay checkout. On success
 * the backend marks the payment captured and advances the workflow, so we
 * invalidate the payment status plus the affected service request (its state
 * moves on) and the list.
 */
export function useVerifyPayment(
  options?: UseMutationOptions<
    VerifyPaymentResponse,
    ApiError,
    { serviceRequestId: string; payload: VerifyPaymentRequest }
  >,
) {
  return useMutation<
    VerifyPaymentResponse,
    ApiError,
    { serviceRequestId: string; payload: VerifyPaymentRequest }
  >({
    mutationFn: ({ payload }) => verifyPayment(payload),
    ...options,
    onSettled: (data, error, vars, onMutateResult, context) => {
      // Also on error: the webhook may still capture the payment.
      queryClient.invalidateQueries({ queryKey: paymentKeys.status(vars.serviceRequestId) });
      queryClient.invalidateQueries({ queryKey: serviceRequestKeys.detail(vars.serviceRequestId) });
      queryClient.invalidateQueries({ queryKey: serviceRequestKeys.list() });
      invalidateBills(vars.serviceRequestId); // capture issues the invoice
      options?.onSettled?.(data, error, vars, onMutateResult, context);
    },
  });
}

/** The advance on a service request, or null when the admin hasn't set one. */
export function useAdvance(
  serviceRequestId: string,
  options?: Omit<UseQueryOptions<AdvanceResponse | null, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<AdvanceResponse | null, ApiError>({
    queryKey: paymentKeys.advance(serviceRequestId),
    queryFn: ({ signal }) => getAdvance(serviceRequestId, signal),
    enabled: Boolean(serviceRequestId),
    ...options,
  });
}

/** Create (or refresh) the Razorpay order for the advance. Call on the pay trigger. */
export function useInitiateAdvance(
  options?: UseMutationOptions<InitiateAdvanceResponse, ApiError, string>,
) {
  return useMutation<InitiateAdvanceResponse, ApiError, string>({
    mutationFn: initiateAdvance,
    ...options,
    onSuccess: (data, serviceRequestId, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.advance(serviceRequestId) });
      options?.onSuccess?.(data, serviceRequestId, onMutateResult, context);
    },
  });
}

/**
 * Verify the advance after Razorpay checkout. On capture the backend issues a
 * receipt voucher and the final checkout amount drops by the advance, so the
 * advance, payment status, bills and the request itself are all refreshed.
 */
export function useVerifyAdvance(
  options?: UseMutationOptions<
    AdvanceResponse,
    ApiError,
    { serviceRequestId: string; payload: VerifyPaymentRequest }
  >,
) {
  return useMutation<
    AdvanceResponse,
    ApiError,
    { serviceRequestId: string; payload: VerifyPaymentRequest }
  >({
    mutationFn: ({ payload }) => verifyAdvance(payload),
    ...options,
    onSettled: (data, error, vars, onMutateResult, context) => {
      // Also on error: the webhook may still capture it.
      queryClient.invalidateQueries({ queryKey: paymentKeys.advance(vars.serviceRequestId) });
      queryClient.invalidateQueries({ queryKey: paymentKeys.status(vars.serviceRequestId) });
      invalidateBills(vars.serviceRequestId);
      queryClient.invalidateQueries({ queryKey: serviceRequestKeys.detail(vars.serviceRequestId) });
      options?.onSettled?.(data, error, vars, onMutateResult, context);
    },
  });
}
